import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useInboxLive } from '../hooks/use-inbox-live';
import { useVisitorPresence } from '../hooks/use-visitor-presence';
import { SAFE_URL } from '../lib/visitor-display';

export interface VisitorPresenceLineProps {
  storeId: string;
  visitorId: string | null;
  customerId: string | null;
  /** The shopper as the thread names them. */
  name: string;
}

/**
 * "Maya is on Aurora solitaire" / "Maya left the site" under the thread, from the store's live
 * visitor list (LC7): heartbeats every 30 s, `visitor.updated` frames, and a 30 s refetch that
 * notices a shopper who has gone (leaving sends nothing).
 */
export function VisitorPresenceLine({
  storeId,
  visitorId,
  customerId,
  name,
}: VisitorPresenceLineProps) {
  const { t } = useTranslation('conversations');
  const live = useInboxLive();
  const presence = useVisitorPresence(storeId, visitorId, customerId, live.polling);
  const visitor = presence.data;
  const [seenOnline, setSeenOnline] = useState(false);
  if (visitor && !seenOnline) setSeenOnline(true);

  if (presence.data === undefined) return null;
  if (!visitor) {
    return (
      <p
        className="text-muted-foreground flex items-center gap-1.5 px-4 pb-1 text-xs"
        aria-live="polite"
        data-testid="visitor-presence"
      >
        <span className="bg-muted-foreground/40 size-1.5 shrink-0 rounded-full" aria-hidden />
        {seenOnline ? t('presence.left', { name }) : t('presence.away', { name })}
      </p>
    );
  }

  const idle = visitor.state === 'idle';
  const label = visitor.page.title?.trim() ? visitor.page.title : visitor.page.url;
  return (
    <p
      className="text-muted-foreground flex min-w-0 items-center gap-1.5 px-4 pb-1 text-xs"
      aria-live="polite"
      data-testid="visitor-presence"
    >
      <span
        className={cn('size-1.5 shrink-0 rounded-full', idle ? 'bg-warning' : 'bg-success')}
        aria-hidden
      />
      <span className="shrink-0">
        {idle ? t('presence.idle', { name }) : t('presence.online', { name })}
      </span>
      {SAFE_URL.test(visitor.page.url) ? (
        <a
          href={visitor.page.url}
          target="_blank"
          rel="noopener noreferrer"
          title={visitor.page.url}
          className="text-foreground truncate underline-offset-2 hover:underline"
        >
          {label}
        </a>
      ) : (
        <span className="text-foreground truncate">{label}</span>
      )}
    </p>
  );
}
